import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { playScanSound } from '../sound'
import { Camera, Scan, WarningCircle } from '../icons'
import { Button, IconButton, cn } from './primitives'
import { Modal } from './overlays'

/**
 * Two engines, same as the runner app: the browser's own BarcodeDetector where it exists (Chrome on Android,
 * Edge, Android WebView) and ZXing — loaded only when the camera opens — everywhere else (every iPhone,
 * Chrome on Windows, Firefox). Nothing here is bundled into a page that never scans.
 */
export const scannerSupported = () => typeof window !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia)

interface DetectedBarcode {
  rawValue: string
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>
}
declare global {
  interface Window {
    BarcodeDetector?: (new (options?: { formats?: string[] }) => BarcodeDetectorLike) & { getSupportedFormats?: () => Promise<string[]> }
  }
}

const REPEAT_GUARD_MS = 1500
const FORMATS = ['code_128', 'code_39', 'codabar', 'ean_13', 'ean_8', 'itf', 'qr_code']
// asking for "the default camera" gets the selfie one on most phones — the back camera is requested first
const CONSTRAINTS: MediaStreamConstraints[] = [
  { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } } },
  { video: { facingMode: 'environment' } },
  { video: true },
]
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * A camera-based AWB scanner: opens the back camera, decodes barcodes/QR codes live, and calls `onDetect`
 * for each new code (the same code within 1.5s is ignored so one scan does not fire twice). With `single`
 * the modal closes itself after the first code — the right behaviour for a search box.
 */
/**
 * What the screen behind the camera made of each scanned code (owner, 2026-09-28: "سكان من الموبايل … يوضحلي ان
 * الشحنة اتضربت وشاشة السكان مفتوحة، عشان مش واضح ايه اتضرب وايه لا"). The camera covers the list on a phone, so
 * the answer is shown on the camera itself.
 */
export interface ScanFeedItem { code: string; state: 'pending' | 'ok' | 'bad'; note?: string }

export function BarcodeScannerModal({ open, onOpenChange, onDetect, single = false, title, description, feed }: { open: boolean; onOpenChange: (open: boolean) => void; onDetect: (code: string) => void; single?: boolean; title?: string; description?: string; feed?: ScanFeedItem[] }) {
  const { t, locale } = useT()
  const ar = locale === 'ar'
  const videoRef = useRef<HTMLVideoElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState(false)
  const [engine, setEngine] = useState<'native' | 'zxing' | null>(null)
  const [attempt, setAttempt] = useState(0)
  const lastRef = useRef<{ code: string; at: number }>({ code: '', at: 0 })
  /** The codes scanned while this camera is open, newest first. */
  const [session, setSession] = useState<string[]>([])
  const doneRef = useRef(false)

  useEffect(() => {
    if (!open) return
    doneRef.current = false
    setError(null)
    setEngine(null)
    if (!scannerSupported()) {
      setError(ar ? 'الكاميرا غير متاحة هنا — افتح النظام من رابط https أو من الهاتف.' : 'Camera unavailable here — open the system over https or on a phone.')
      return
    }
    let stream: MediaStream | null = null
    let raf = 0
    let stopped = false
    let zx: { stop?: () => void } | null = null

    const hit = (raw: string) => {
      const value = raw.trim()
      if (!value || doneRef.current) return
      const now = Date.now()
      if (value === lastRef.current.code && now - lastRef.current.at < REPEAT_GUARD_MS) return
      lastRef.current = { code: value, at: now }
      playScanSound()
      if (navigator.vibrate) navigator.vibrate(30)
      setFlash(true)
      setTimeout(() => setFlash(false), 200)
      setSession((s) => [value, ...s.filter((c) => c.toUpperCase() !== value.toUpperCase())].slice(0, 50))
      onDetect(value)
      if (single) {
        doneRef.current = true
        setTimeout(() => onOpenChange(false), 180)
      }
    }

    const release = () => {
      stream?.getTracks().forEach((tr) => tr.stop())
      stream = null
      const el = videoRef.current
      if (el?.srcObject) {
        ;(el.srcObject as MediaStream).getTracks?.().forEach((tr) => tr.stop())
        el.srcObject = null
      }
    }

    const openStream = async () => {
      let last: unknown = null
      for (const c of CONSTRAINTS) {
        // "Could not start video source" = the device is still held by the previous scan; it needs a moment
        for (let go = 0; go < 3; go += 1) {
          try {
            return await navigator.mediaDevices.getUserMedia(c)
          } catch (e) {
            last = e
            const name = (e as Error)?.name
            if (name === 'NotAllowedError' || name === 'SecurityError') throw e
            if (name !== 'NotReadableError' && name !== 'AbortError') break
            release()
            await sleep(400)
            if (stopped) throw e
          }
        }
      }
      throw last || new Error('no camera')
    }

    const runNative = async () => {
      const BD = window.BarcodeDetector!
      const supported = (await BD.getSupportedFormats?.().catch(() => FORMATS)) ?? FORMATS
      const detector = new BD({ formats: FORMATS.filter((f) => supported.includes(f)) })
      stream = await openStream()
      if (stopped) return release()
      const video = videoRef.current
      if (!video) return
      video.srcObject = stream
      try {
        await video.play()
      } catch (e) {
        if ((e as Error)?.name !== 'AbortError' && (e as Error)?.name !== 'NotAllowedError') throw e
      }
      setEngine('native')
      const tick = async () => {
        if (stopped || !videoRef.current) return
        if (videoRef.current.readyState >= 2) {
          try {
            const codes = await detector.detect(videoRef.current)
            if (codes[0]?.rawValue) hit(codes[0].rawValue)
          } catch {
            /* a frame that fails to decode is normal — just try the next one */
          }
        }
        raf = requestAnimationFrame(() => void tick())
      }
      raf = requestAnimationFrame(() => void tick())
    }

    const runZxing = async () => {
      const { BrowserMultiFormatReader } = await import('@zxing/browser')
      if (stopped || !videoRef.current) return
      setEngine('zxing')
      const reader = new BrowserMultiFormatReader()
      release() // it drives the camera itself
      let last: unknown = null
      for (const c of CONSTRAINTS) {
        try {
          zx = await reader.decodeFromConstraints(c, videoRef.current, (result) => {
            if (result) hit(result.getText())
          })
          return
        } catch (e) {
          last = e
          const name = (e as Error)?.name
          if (name === 'NotAllowedError' || name === 'SecurityError') throw e
        }
      }
      throw last || new Error('no camera')
    }

    void (async () => {
      try {
        if ('BarcodeDetector' in window) await runNative()
        else await runZxing()
      } catch (e) {
        if (stopped) return
        const name = (e as Error)?.name || ''
        if (name === 'NotAllowedError' || name === 'SecurityError') setError(ar ? 'تعذر الوصول إلى الكاميرا — اسمح للمتصفح باستخدام الكاميرا ثم أعد المحاولة.' : 'Could not access the camera — allow camera permission and retry.')
        else if (name === 'NotReadableError' || name === 'AbortError') setError(ar ? 'الكاميرا مستخدمة في تطبيق آخر — أغلقه ثم أعد المحاولة.' : 'The camera is busy in another app — close it and retry.')
        else setError(ar ? 'لا توجد كاميرا متاحة على هذا الجهاز.' : 'No camera is available on this device.')
      }
    })()

    return () => {
      stopped = true
      cancelAnimationFrame(raf)
      try {
        zx?.stop?.()
      } catch {
        /* already gone */
      }
      release()
      const el = videoRef.current
      if (el) {
        el.pause?.()
        el.srcObject = null
      }
    }
  }, [open, ar, attempt, single, onDetect, onOpenChange])

  return (
    <Modal open={open} onOpenChange={onOpenChange} size="sm" title={title ?? (ar ? 'المسح بالكاميرا' : 'Camera scan')} description={description ?? (single ? (ar ? 'وجه الكاميرا على باركود الشحنة' : 'Point the camera at the shipment barcode') : ar ? 'وجه الكاميرا على الباركود — تضاف كل شحنة تلقائيا' : 'Point the camera at a barcode — each shipment is added automatically')}>
      {error ? (
        <div className="flex flex-col items-center gap-3 px-2 py-10 text-center">
          <WarningCircle size={28} className="text-warning" aria-hidden />
          <p className="text-sm text-fg-muted">{error}</p>
          <Button size="sm" variant="secondary" icon={Camera} onClick={() => setAttempt((n) => n + 1)}>{ar ? 'إعادة المحاولة' : 'Retry'}</Button>
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-xl bg-black">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video ref={videoRef} muted playsInline autoPlay className="aspect-[4/3] w-full object-cover" />
          {!engine && <div className="absolute inset-0 grid place-items-center text-xs text-white/70">{ar ? 'جار فتح الكاميرا…' : 'Opening camera…'}</div>}
          <div className={cn('pointer-events-none absolute inset-0 border-4 transition-colors duration-150', flash ? 'border-success' : 'border-transparent')} aria-hidden />
          <div className="pointer-events-none absolute inset-[18%] rounded-xl border-2 border-white/60 [box-shadow:0_0_0_999px_rgba(0,0,0,.35)]" aria-hidden />
          <div className="scan-line pointer-events-none absolute inset-x-[20%] h-0.5 rounded bg-brand" aria-hidden />
          {!single && <ScanMark code={session[0]} feed={feed} ar={ar} />}
        </div>
      )}
      {!single && session.length > 0 && <ScanSessionList session={session} feed={feed} ar={ar} />}
      <div className="mt-4 flex justify-end">
        <Button icon={Camera} onClick={() => onOpenChange(false)}>{t('common.close')}</Button>
      </div>
    </Modal>
  )
}

/**
 * The scan button that sits inside every AWB box: one tap opens the camera, the first barcode is handed to
 * `onScan` (usually "set the field and search"), and the camera closes. `multi` keeps it open for lists.
 */
/**
 * The answer for the last scan, big, in the middle of the camera (owner, 2026-09-28: "لو الشحنة اتضربت من سكان
 * الموبايل طلعله علامة صح في وسط السكانر عشان يفهم"): ✓ when it went on, ✗ when it was refused, a spinner while the
 * screen is still asking. It fades after two seconds so the next barcode can be aimed at.
 */
export function ScanMark({ code, feed, ar }: { code?: string; feed?: ScanFeedItem[]; ar: boolean }) {
  const f = code ? feed?.find((x) => x.code.toUpperCase() === code.toUpperCase()) : undefined
  const state: ScanFeedItem['state'] | null = !code ? null : feed ? f?.state ?? 'pending' : 'ok'
  const [shownFor, setShownFor] = useState('')
  const key = code && state ? `${code}|${state}` : ''
  useEffect(() => {
    if (!key) return
    setShownFor(key)
    if (state === 'pending') return
    const t = setTimeout(() => setShownFor((k) => (k === key ? '' : k)), 2000)
    return () => clearTimeout(t)
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!key || shownFor !== key) return null
  const look = state === 'ok' ? 'bg-success text-white' : state === 'bad' ? 'bg-danger text-white' : 'bg-black/60 text-white'
  return (
    <div className="pointer-events-none absolute inset-0 grid place-items-center" role="status" aria-label={state === 'ok' ? (ar ? 'تمت الإضافة' : 'Added') : state === 'bad' ? (ar ? 'مرفوضة' : 'Refused') : (ar ? 'جار التحقق' : 'Checking')}>
      <div className="flex flex-col items-center gap-2">
        <span className={cn('grid size-24 place-items-center rounded-full text-6xl font-black shadow-xl', look, state === 'pending' && 'animate-pulse')}>{state === 'ok' ? '✓' : state === 'bad' ? '✗' : '…'}</span>
        <span className="rounded-full bg-black/60 px-3 py-1 font-mono text-sm font-semibold text-white" dir="ltr">{code}</span>
        {state === 'bad' && f?.note && <span className="max-w-[85%] rounded-lg bg-black/70 px-3 py-1 text-center text-xs text-white">{f.note}</span>}
      </div>
    </div>
  )
}

/** Each code scanned in this camera session with what the screen made of it — the newest on top. */
export function ScanSessionList({ session, feed, ar }: { session: string[]; feed?: ScanFeedItem[]; ar: boolean }) {
  const of = (code: string) => feed?.find((f) => f.code.toUpperCase() === code.toUpperCase())
  const done = session.filter((c) => of(c)?.state === 'ok').length
  const bad = session.filter((c) => of(c)?.state === 'bad').length
  return (
    <div className="mt-3" aria-live="polite">
      <p className="mb-1.5 text-sm font-semibold">
        {ar ? `تم مسح ${session.length}` : `Scanned ${session.length}`}
        {feed && <span className="ms-2 text-success">{ar ? `تمت إضافة ${done}` : `added ${done}`}</span>}
        {feed && bad > 0 && <span className="ms-2 text-danger">{ar ? `مرفوضة ${bad}` : `refused ${bad}`}</span>}
      </p>
      <ul className="max-h-44 space-y-1 overflow-y-auto">
        {session.slice(0, 20).map((code, i) => {
          const f = of(code)
          const state = f?.state ?? (feed ? 'pending' : 'ok')
          const tone = state === 'ok' ? 'border-success/40 bg-success-soft text-success' : state === 'bad' ? 'border-danger/40 bg-danger-soft text-danger' : 'border-border bg-bg-subtle text-fg-muted'
          const word = state === 'ok' ? (feed ? (ar ? 'تمت الإضافة ✓' : 'Added ✓') : (ar ? 'تم الإرسال' : 'Sent')) : state === 'bad' ? (ar ? 'مرفوضة ✗' : 'Refused ✗') : (ar ? 'جار التحقق…' : 'Checking…')
          return (
            <li key={code} className={cn('flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm', tone, i === 0 && 'ring-2 ring-brand/40')}>
              <span className="font-mono font-semibold" dir="ltr">{code}</span>
              <span className="ms-auto shrink-0 font-bold">{word}</span>
              {state === 'bad' && f?.note && <span className="basis-full text-xs">{f.note}</span>}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function ScanButton({ onScan, multi = false, size = 'sm', variant = 'ghost', className, label, children, feed }: { onScan: (code: string) => void; multi?: boolean; size?: 'xs' | 'sm' | 'md' | 'lg'; variant?: 'ghost' | 'secondary' | 'primary'; className?: string; label?: string; children?: ReactNode; feed?: ScanFeedItem[] }) {
  const { locale } = useT()
  const ar = locale === 'ar'
  const [open, setOpen] = useState(false)
  const text = label ?? (ar ? 'مسح ضوئي' : 'Scan')
  return (
    <>
      {children ? (
        <Button type="button" size={size} variant={variant} icon={Scan} className={className} onClick={() => setOpen(true)}>{children}</Button>
      ) : (
        <IconButton type="button" size={size} variant={variant} icon={Scan} label={text} className={cn('text-brand', className)} onClick={() => setOpen(true)} />
      )}
      {open && <BarcodeScannerModal open={open} onOpenChange={setOpen} onDetect={onScan} single={!multi} feed={feed} />}
    </>
  )
}
